
import { Component, Mixins, Watch } from 'vue-property-decorator'
import IconsMixin from '@/mixins/IconsMixin.vue'
import AuthMixin from '@/mixins/AuthMixin.vue'
import UserCompaniesMixin from '@/mixins/UserCompaniesMixin.vue'
import LoadingPage from '@/components/LoadingPage.vue'
import {
  Company,
  CompanyCollection,
  CompanyTypeEnum,
  CompanyStatusEnum
} from '@/firestore'
import * as Sentry from '@sentry/vue'
import axios from 'axios'
import moment from 'moment'
import SaveButton from '@/components/SaveButton.vue'
import AddressEdit from '@/components/Address/AddressEdit.vue'
import store, { Mutations, Actions } from '../../../store'
import { Mutation, Action } from 'vuex-class'
import LabeledValue from '@/components/LabeledValue.vue'
import LiquorLicenseFields from '@/components/LiquorLicense/LiquorLicenseFields.vue'
import { LicenseInfo, licenseService } from '@/components/LiquorLicense/liquor-license'

@Component({
  components: {
    SaveButton,
    LoadingPage,
    AddressEdit,
    LabeledValue,
    LiquorLicenseFields
  }
})
export default class RestaurantDetails extends Mixins(IconsMixin, AuthMixin, UserCompaniesMixin) {
  @Mutation(Mutations.UPDATE_COMPANIES)
  public updateCompanies: any

  @Action(Actions.SET_USER_COMPANY)
  public setUserCompany: any

  public action: 'create' | 'modify' | '' = ''
  public restaurant: Company | null = null
  public licenseLoading = false
  public licenseInfo: LicenseInfo | false | null = null
  public licenseNumber = ''
  public existingLicenseFile: File | null = null
  public licenseFile: File | null = null
  public restaurantLimitReached = false

  public isSaving: boolean = false

  public restaurantStatus = CompanyStatusEnum
  public restaurantStatusLoading: null | 'primary' = null

  public modalError = false
  public modalErrorMessage = ''

  public $refs!: {
    form: HTMLFormElement
  }

  public async mounted() {
    if (this.$route.params.id === 'create') {
      this.action = 'create'

      const restaurantLimitCapacity = await this.restaurantLimitCapacity()
      if (restaurantLimitCapacity === 0) {
        this.restaurantLimitReached = true
        return
      }

      this.restaurant = this.createRestaurant()
    } else {
      this.action = 'modify'
      try {
        this.restaurant = await this.lookupRestaurant(this.$route.params.id)
        this.licenseLoading = true
        const licenseInfo = await licenseService.info(this.$route.params.id)
        this.updateLicenseInfo(licenseInfo || false)
        this.licenseLoading = false
      } catch (error) {
        const err = new Error('You do not have permissions to view that page')
        return store.dispatch(Actions.SET_ERROR, [err, this.$router])
      }
    }

    if (!this.restaurant) {
      const err = new Error('You do not have permissions to view that page')
      return store.dispatch(Actions.SET_ERROR, [err, this.$router])
    }
  }

  public async save() {
    if (!this.$refs.form.validate()) {
      return
    }

    this.isSaving = true

    await this.updateRestaurantName()
    await this.updateLiquorLicense()

    if (this.restaurant && this.$route.params.id === 'create') {
      this.setUserCompany(this.restaurant.id)
    }

    await this.$router.push({name: 'home'})
    this.isSaving = false
  }

  @Watch('restaurant.status')
  public async updateRestaurantStatus(
      _newStatus: CompanyStatusEnum,
      oldStatus: CompanyStatusEnum
  ) {
    if (
        !this.restaurant
        || this.$route.params.id !== this.restaurant.id
        || typeof oldStatus === 'undefined'
    ) {
      return
    }

    if (this.restaurant.status === CompanyStatusEnum.ACTIVE) {
      const restaurantLimitCapacity = await this.restaurantLimitCapacity()
      if (restaurantLimitCapacity === 0) {
        this.showError('You have reached the maximum number of restaurants in your subscription plan')
        this.restaurant.status = CompanyStatusEnum.INACTIVE
        return
      }
    }

    this.restaurantStatusLoading = 'primary'

    const adminCompanyId = this.restaurant.adminCompanyId
        || (
            this.userCompany.type === CompanyTypeEnum.ADMIN
                ? this.userCompany.id
                : this.userCompany.adminCompanyId
        )

    try {
      await axios.put(`/api/restaurants/${this.restaurant.id}`, {
        adminCompanyId,
        status: this.restaurant.status
      })
    } catch (error) {
      Sentry.captureException(error)
      this.restaurant.status = this.restaurant.status === CompanyStatusEnum.ACTIVE
          ? CompanyStatusEnum.INACTIVE
          : CompanyStatusEnum.ACTIVE
      this.showError(`An error occured while updating the restaurant's status`)
    }

    this.restaurantStatusLoading = null
  }

  private createRestaurant(): Company {
    return new Company('', CompanyTypeEnum.RESTAURANT)
  }

  protected async updateRestaurantName() {
    if (!this.restaurant) {
      return
    }

    const adminCompanyId = this.restaurant.adminCompanyId
    || this.userCompany.type === CompanyTypeEnum.ADMIN
        ? this.userCompany.id
        : this.userCompany.adminCompanyId

    const payload = {...this.restaurant, adminCompanyId}

    this.restaurant = this.$route.params.id === this.restaurant.id
        ? (await axios.put(`/api/restaurants/${this.restaurant.id}`, payload)).data
        : (await axios.post('/api/restaurants', payload)).data

    if (this.restaurant) {
      this.updateCompanies({companies: {...this.userCompanies, [this.restaurant.id]: this.restaurant}})
    }
  }

  protected async updateLiquorLicense() {
    if (
        !this.restaurant
        || !this.licenseNumber
        || !this.licenseFile
    ) {
      return
    }

    const licenseInfo = await licenseService.save(
        this.restaurant.id,
        this.licenseNumber,
        this.licenseFile === this.existingLicenseFile && this.licenseInfo
            ? this.licenseInfo.fileId
            : this.licenseFile
    )
    this.updateLicenseInfo(licenseInfo)
  }

  private async lookupRestaurant(id: string) {
    const companyCollection = new CompanyCollection((await this.$firebase).firestore())
    return await companyCollection.getCompany(id)
  }

  private updateLicenseInfo(licenseInfo: LicenseInfo | false) {
    this.licenseInfo = licenseInfo
    if (licenseInfo) {
      this.existingLicenseFile = licenseService.placeholderFile(
          licenseInfo.filename,
          licenseInfo.mimeType,
          +licenseInfo.size,
          licenseInfo.createdAt
      )
      this.licenseFile = this.existingLicenseFile
      this.licenseNumber = licenseInfo.licenseNumber
    } else {
      this.existingLicenseFile = null
      this.licenseFile = null
      this.licenseNumber = ''
    }
  }

  private async restaurantLimitCapacity() {
    const companyCollection = new CompanyCollection((await this.$firebase).firestore())
    let adminCompany = this.userCompany
    let restaurants: Company[] = []

    if (this.userCompany.type === 'admin') {
      restaurants = await companyCollection.listCompaniesByAdminCompany(this.userCompany.id)
    } else if (this.userCompany.type === 'restaurant' && this.userCompany.adminCompanyId) {
      const company = await companyCollection.getCompany(this.userCompany.adminCompanyId)
      if (company) {
        adminCompany = company
      } else {
        this.restaurantLimitReached = true
        return
      }
      restaurants = await companyCollection.listCompaniesByAdminCompany(this.userCompany.adminCompanyId)
    }

    const activeRestaurantCount = restaurants
        .filter(r => r.status === CompanyStatusEnum.ACTIVE)
        .length

    return (
        typeof adminCompany.restaurantLimit === 'number'
        && adminCompany.restaurantLimit !== -1
    )
        ? Math.max(adminCompany.restaurantLimit - activeRestaurantCount, 0)
        : -1
  }

  public showError(message: string) {
    this.modalErrorMessage = message
    this.modalError = true
  }

  public hideError() {
    this.modalError = false
    this.modalErrorMessage = ''
  }
}
